import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import {
  FacilityInformation,
  FacilityProcessDetails,
  FacilityFarmCapacityInformation,
} from "../../../sc-models/facility-product-dto";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { useParams } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { ALL_FARM_CATEGORIES, FarmCategoryUtils } from "../../../constants/farm-categories";

interface RawMaterialsInfoProps {
  facilityData: FacilityInformation;
  facilityProcessInfo: FacilityProcessDetails[]; // Expecting an array
  facilityFarmCapacityInfo: FacilityFarmCapacityInformation[]; // Added for validation
  setFacilityProcessInfo: React.Dispatch<
    React.SetStateAction<FacilityProcessDetails[]>
  >;
  setSubsequentFacilityFlag: React.Dispatch<React.SetStateAction<boolean>>;
  setScSubsequentFacilityButtonFlag: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  scNoOfFarmsFlag: boolean;
  setScNoOfFarmsFlag: React.Dispatch<React.SetStateAction<boolean>>;
  isFarmSite: boolean;
  setIsFarmSite: React.Dispatch<React.SetStateAction<boolean>>;
  subsequentFacilityFlag: boolean;
  scSubsequentFacilityButtonFlag: boolean;
}
const farmCategories = ALL_FARM_CATEGORIES;

const FacilityProcessInfo: React.FC<RawMaterialsInfoProps> = ({
  facilityData,
  facilityProcessInfo,
  facilityFarmCapacityInfo,
  setFacilityProcessInfo,
  setSubsequentFacilityFlag,
  setScSubsequentFacilityButtonFlag,
  scNoOfFarmsFlag,
  setScNoOfFarmsFlag,
  subsequentFacilityFlag,
  scSubsequentFacilityButtonFlag,
  isFarmSite,
  setIsFarmSite,
}) => {
  const { updateField } = useFormActions("sc-facilitydetails");
  const [data, setData] =
    useState<FacilityProcessDetails[]>(facilityProcessInfo); // Initialize with prop data
  const [tableData, setTableData] =
    useState<FacilityProcessDetails[]>(facilityProcessInfo); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [facilityProcessCategoryCode, setFacilityProcessCategoryCode] =
    useState<any[]>([]);
  const { executeGet } = useApi();
  const { SC_ID } = useParams();
  const {
    isRestrictPopUpSave,
    setRestrictPopUpSave,
    setFacilityProcessCategoryCodes,
  } = useTransactionCertificateStore();
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  const { notify } = useNotification();

  // If scNoOfFarmsFlag is false (not a farm SC), hide PR0011, PR0036, PR0038
  const filteredFacilityProcessCategoryCode: any[] = facilityProcessCategoryCode.filter((option: any) => {
    if (!scNoOfFarmsFlag && ["PR0011", "PR0036", "PR0038"].includes(option.value)) {
      return false;
    }
    return true;
  });

  const fetchSCStandardDetails = async () => {
    const result = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, {
      scId: SC_ID,
    });
    console.log("result", result?.data);
    if (result?.data?.scStandardInformation?.length > 0) {
      const standards = result?.data.scStandardInformation
        .filter(
          (detail: any) =>
            detail.numberOfAuditDays === 0
        )
        .map((detail: any) => detail.scStandard);
      // console.log("standards", standards)
      setSelectedStandards(standards);
      // Check scNumberOfFarms from scope certificate details instead of standard details
      const scNoOfFarms = result?.data.scNumberOfFarms > 0;
      console.log("scNoOfFarms-->", scNoOfFarms);
      setScNoOfFarmsFlag(scNoOfFarms);
    }
    return result;
  };

  const getValidationMessage = (updatedRow: FacilityProcessDetails) => {
    const hasFarmCategory = FarmCategoryUtils.isFarmCode(
      updatedRow.facilityProcessCategoryCode
    );

    // Check if any farm capacity entry has farm animals data
  const hasFarmAnimalsData = facilityFarmCapacityInfo.some(
      (item) => item.facilityFarmRawMaterialNumberOfAnimals && item.facilityFarmRawMaterialNumberOfAnimals !== "0"
    );

    if (!hasFarmCategory) return null;

    if (
      selectedStandards.includes("RDS") &&
      (selectedStandards.includes("RWS") ||
        selectedStandards.includes("RMS") ||
        selectedStandards.includes("RAS"))
    ) {
      return "For RDS/RAF standards with farm categories, please specify the Number of Farms in the Scope Certificate Details section.";
    }
    if (selectedStandards.includes("RDS")) {
      return "For RDS standard with farm categories, please specify the Number of Audit Days in the Standard Details section.";
    }
    return null;
  };

  const setSubmissionFlag = (updatedRow: FacilityProcessDetails) => {
    const hasFarmCategory = FarmCategoryUtils.isFarmCode(
      updatedRow.facilityProcessCategoryCode
    );
  };

  useEffect(() => {
    setRestrictPopUpSave(false);
    if (SC_ID) {
      fetchSCStandardDetails();
    }
    return () => {
      setRestrictPopUpSave(false);
    };
  }, []);
  const validationSchema = Yup.object().shape({
    facilityProcessCategoryCode: Yup.string().required(
      "Process Category Code is required"
    ),
    facilityProcessCategoryUserSpecificTerm: Yup.string().when("facilityProcessCategoryCode", {
      is: "PR0034",
      then: (schema) => schema
        .transform((value) => (value ? value.trim() : value))
        .required("User Specific Term is required for PR0034")
        .test("no-na", "na, n/a, not available, or null are not allowed", (value) => {
          if (!value) return false;
          const cleanValue = value.toLowerCase().replace(/[^a-z]/g, '');
          const forbidden = ["na", "notapplicable", "notavailable", "none", "null"];
          return !forbidden.includes(cleanValue);
        }),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any =
          await dtrackItServiceV4.getFacilityProcessCategoryCodes();
        const rawMaterials = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setFacilityProcessCategoryCode(rawMaterials);
        setFacilityProcessCategoryCodes(rawMaterials);
      } catch (err) {
        console.log(`Error in Fetching Organic Farm Standard Code ${err}`);
      }
    };

    getRawMaterialCodes();
  }, []);
  useEffect(() => {
    updateField("facilityProcessDetails", data);
  }, [data]);

  // Initialize isFarmSite when component loads with existing data
  useEffect(() => {
    if (facilityProcessInfo && facilityProcessInfo.length > 0) {
      const hasFarmCategory = facilityProcessInfo.some((process) =>
        FarmCategoryUtils.isFarmCode(process?.facilityProcessCategoryCode)
      );
      setIsFarmSite(hasFarmCategory);
    }
  }, [facilityProcessInfo, setIsFarmSite]);

  const formik = useFormik<FacilityProcessDetails>({
    initialValues: {
      facilityProcessCategoryCode: "",
      facilityProcessCategoryCodeDescription: "",
      facilityProcessCategoryUserSpecificTerm: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    // if (columnId === 'facilityProcessCategoryCode') {
    //   formik.setFieldValue(columnId, value);
      
    //   // Set the description field when dropdown value changes
    //   const selectedOption = filteredFacilityProcessCategoryCode.find((option: any) => option.value === value);
    //   if (selectedOption) {
    //     formik.setFieldValue('facilityProcessCategoryCodeDescription', selectedOption.label);
        
    //     // Auto-fill User Specific Term with "na" if code is not PR0034 (Others)
    //     const userSpecificTerm = value !== 'PR0034' ? 'na' : '';
    //     formik.setFieldValue('facilityProcessCategoryUserSpecificTerm', userSpecificTerm);
        
    //     setData((prevData) =>
    //       prevData.map((row, index) =>
    //         index === rowIndex ? { 
    //           ...row, 
    //           [columnId]: value, 
    //           facilityProcessCategoryCodeDescription: selectedOption.label,
    //           facilityProcessCategoryUserSpecificTerm: userSpecificTerm
    //         } : row
    //       )
    //     );
    //   } else {
    //     setData((prevData) =>
    //       prevData.map((row, index) =>
    //         index === rowIndex ? { ...row, [columnId]: value } : row
    //       )
    //     );
    //   }
    // } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    // }
  };

  const handleAddNewRow = () => {
    const newRow: any = {
      facilityProcessCategoryCode: "",
      facilityProcessCategoryCodeDescription: "",
      facilityProcessCategoryUserSpecificTerm: "",
    };
    setRestrictPopUpSave(true);
    const newData = [...data, newRow];
    setData(newData);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
    formik.setErrors({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;

    // // Determine if the row being edited is new by comparing `editingRowIndex` with the data length
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }

    setData(tableData);

    // Reset editing state and form
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null || editingRowIndex === -1) {
      notify('No row selected for editing', 'error');
      return;
    }

    // Check if required fields are filled in the actual data
    const currentRowData = data[editingRowIndex];
    if (!currentRowData.facilityProcessCategoryCode) {
      notify('Facility Process Category Code is required', 'error');
      return;
    }

    if (!currentRowData.facilityProcessCategoryUserSpecificTerm?.trim() && currentRowData.facilityProcessCategoryCode==='PR0034') {
      notify('Facility Process Category User Specific Term is required for Facility Process Category Code PR0034', 'error');
      return;
    }
    if(currentRowData?.facilityProcessCategoryUserSpecificTerm.trim() && !Utills?.restrictNA(currentRowData?.facilityProcessCategoryUserSpecificTerm.trim())){
      notify(`Facility Process Category User Specific Term cannot have value ${currentRowData?.facilityProcessCategoryUserSpecificTerm}`);
      return;
    }
    // Check for duplicate facility process category codes
    const currentProcessCategoryCode = String(currentRowData.facilityProcessCategoryCode);
    const duplicateExists = data.some((row, index) => 
      index !== editingRowIndex && 
      row.facilityProcessCategoryCode && 
      String(row.facilityProcessCategoryCode) === currentProcessCategoryCode
    );
    
    if (duplicateExists) {
      notify('Facility Process Category Code must be unique across all rows', 'error');
      return;
    }

    try {
      // Use the current row data instead of formik values to ensure we have the latest data
      const updatedRow = currentRowData;
      
      // Show info notification if specific farm codes are selected but SC Number of Farms is 0 or null
      // PR0037 is excluded per new business rule
      const requiresScNoOfFarms = ["PR0011", "PR0036", "PR0038"].includes(updatedRow.facilityProcessCategoryCode);
      if (!scNoOfFarmsFlag && requiresScNoOfFarms) {
        notify("Info: Farm process category selected but SC Number of Farms is 0. Please update the Number of Farms in Standard Details", 'info');
      }
      
      setSubmissionFlag(updatedRow);
      const message = getValidationMessage(updatedRow);

      const newData = data?.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
      let hasFarmCategory = false;
      newData?.forEach((value) => {
        if (FarmCategoryUtils.isFarmCode(value.facilityProcessCategoryCode)) {      
          hasFarmCategory = true;
        }
      });
      setIsFarmSite(hasFarmCategory);

      setData(newData);
      setTableData(newData);
      setFacilityProcessInfo(newData); // Update parent with updated data
      setRestrictPopUpSave(false);
      setEditingRowIndex(-1);
      formik.resetForm();
      
      notify('Facility process details saved successfully', 'success');
    } catch (error) {
      console.error('Error saving facility process details:', error);
      notify('Error saving facility process details', 'error');
    }
  };

  const columns: EditableColumnDef<FacilityProcessDetails>[] = [
    {
      accessorKey: "facilityProcessCategoryCode",
      header: "Facility Process Category Code",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Facility Process Category Code
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="facilityProcessCategoryCode"
          value={value}
          placeholder="Enter value"
          options={filteredFacilityProcessCategoryCode}
          enableSearch={true}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ),
    },
    {
      accessorKey: "facilityProcessCategoryUserSpecificTerm",
      header: "Facility Process Category User Specific Term",
      Header: (
        <span>
          {" "}
          Facility Process Category User Specific Term
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="facilityProcessCategoryUserSpecificTerm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    
    // Ensure all required fields are set in formik including description
    const rowData = data[rowIndex];
    const formikValues = {
      facilityProcessCategoryCode: rowData.facilityProcessCategoryCode || "",
      facilityProcessCategoryCodeDescription: rowData.facilityProcessCategoryCodeDescription || "",
      facilityProcessCategoryUserSpecificTerm: rowData.facilityProcessCategoryUserSpecificTerm || "",
    };
    
    formik.setValues(formikValues);
    formik.setTouched({});
    formik.setErrors({});
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    if (!subsequentFacilityFlag && !scSubsequentFacilityButtonFlag) {
      setSubsequentFacilityFlag(true);
    }
    setData(updatedData);
    setTableData(updatedData);
    setFacilityProcessInfo(updatedData); // Update parent with updated data
    
    // Update isFarmSite after deletion
    const hasFarmCategory = updatedData.some((process) =>
      FarmCategoryUtils.isFarmCode(process?.facilityProcessCategoryCode)
    );
    setIsFarmSite(hasFarmCategory);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          border: "2px solid rgba(128, 128, 128, .2)",
          my: 4,
        }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton
            disabled={!formik.isValid || isRestrictPopUpSave}
            onClick={handleAddNewRow}
            sx={{ mb: 2 }}
          >
            Add Facility Process Information
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default FacilityProcessInfo;
